import { memo, useEffect, useRef, useState } from 'react';
import { Languages, Loader2, Moon, Sun } from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUISettings } from '../context/UISettingsContext';
import FluidBackground from '../components/FluidBackground';
import CookieBanner from '../components/CookieBanner';

const UserBar = memo(({ profile, onSignOut, onNavigate, theme, onThemeToggle, language, onLanguageToggle, isLanguageSwitching, t }: any) => {
  const isDark = theme === 'dark';
  const sharedButtonClass =
    'btn-premium app-panel flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-[var(--app-text-muted)] shadow-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-[var(--app-accent)] dark:bg-slate-950/62 dark:border-slate-700/60 dark:text-slate-300 dark:hover:bg-slate-900/88 dark:hover:text-slate-100 dark:hover:border-slate-700/60';

  const controls = (
    <>
      <button data-testid="layout-theme-toggle" onClick={onThemeToggle} className={sharedButtonClass} aria-label={t('layout.theme')}>
        {theme === 'dark' ? <Sun size={12} /> : <Moon size={12} />}
        <span>{theme === 'dark' ? t('layout.themeLight') : t('layout.themeDark')}</span>
      </button>

      <button data-testid="layout-language-toggle" onClick={onLanguageToggle} className={sharedButtonClass} aria-label={t('layout.language')} disabled={isLanguageSwitching}>
        {isLanguageSwitching ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
        <span>{language === 'pl' ? t('layout.languageEnglish') : t('layout.languagePolish')}</span>
      </button>
    </>
  );

  if (!profile) {
    return <div className="absolute right-3 top-3 z-50 flex flex-wrap items-center justify-end gap-2">{controls}</div>;
  }

  return (
    <div className="absolute right-3 top-3 z-50 flex flex-wrap items-center justify-end gap-2">
      {controls}

      {profile.isAdmin && (
        <button
          data-testid="layout-admin-btn"
          onClick={() => onNavigate('/admin')}
          className={`${sharedButtonClass} px-3`}
          style={
            isDark
              ? {
                  borderColor: 'rgba(99, 102, 241, 0.28)',
                  background: 'linear-gradient(180deg, rgba(30,41,59,0.96), rgba(15,23,42,1))',
                  color: '#e0e7ff',
                  boxShadow: '0 10px 28px -20px rgba(99,102,241,0.8)',
                }
              : undefined
          }
        >
          {t('layout.admin')}
        </button>
      )}

      <button data-testid="layout-logout-btn" onClick={onSignOut} className={sharedButtonClass}>
        {t('layout.signOut')}
      </button>
    </div>
  );
});

export default function MainLayout() {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, resolvedTheme, setThemePreference, t } = useUISettings();
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const languageSwitchTimeoutRef = useRef<number | null>(null);
  const cursorPrimaryRef = useRef<HTMLDivElement | null>(null);
  const cursorSecondaryRef = useRef<HTMLDivElement | null>(null);
  const cursorFrameRef = useRef<number | null>(null);
  const cursorTargetRef = useRef({ x: 0, y: 0, active: false });
  const cursorStateRef = useRef({ x: 0, y: 0, x2: 0, y2: 0, initialized: false });

  useEffect(() => {
    const setCenterTarget = () => {
      cursorTargetRef.current.x = window.innerWidth / 2;
      cursorTargetRef.current.y = window.innerHeight / 2;
      cursorTargetRef.current.active = false;
    };
    setCenterTarget();

    const animate = () => {
      const primary = cursorPrimaryRef.current;
      const secondary = cursorSecondaryRef.current;
      const state = cursorStateRef.current;
      const target = cursorTargetRef.current;

      if (!primary || !secondary) {
        cursorFrameRef.current = window.requestAnimationFrame(animate);
        return;
      }

      if (!state.initialized) {
        state.x = target.x;
        state.y = target.y;
        state.x2 = target.x;
        state.y2 = target.y;
        state.initialized = true;
      }

      state.x += (target.x - state.x) * 0.18;
      state.y += (target.y - state.y) * 0.18;
      state.x2 += (target.x - state.x2) * 0.1;
      state.y2 += (target.y - state.y2) * 0.1;

      primary.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) translate(-50%, -50%)`;
      secondary.style.transform = `translate3d(${state.x2}px, ${state.y2}px, 0) translate(-50%, -50%)`;

      const opacity = target.active ? '1' : '0.55';
      primary.style.opacity = opacity;
      secondary.style.opacity = opacity;

      cursorFrameRef.current = window.requestAnimationFrame(animate);
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') {
        return;
      }
      cursorTargetRef.current.x = event.clientX;
      cursorTargetRef.current.y = event.clientY;
      cursorTargetRef.current.active = true;
    };

    const handlePointerLeave = () => {
      setCenterTarget();
    };

    const handleResize = () => {
      if (!cursorTargetRef.current.active) {
        setCenterTarget();
      }
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', handlePointerLeave, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    cursorFrameRef.current = window.requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('resize', handleResize);
      if (cursorFrameRef.current !== null) {
        window.cancelAnimationFrame(cursorFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (languageSwitchTimeoutRef.current) {
        window.clearTimeout(languageSwitchTimeoutRef.current);
      }
    };
  }, []);

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    if (path === '/admin' && location.pathname.startsWith('/results/')) {
      const backParam = encodeURIComponent(location.pathname);
      navigate(`/admin?backToResultsPath=${backParam}`, {
        state: { backToResultsPath: location.pathname },
      });
      return;
    }
    navigate(path);
  };
  const isAdminRoute = location.pathname.startsWith('/admin');
  const cursorPage = location.pathname.startsWith('/admin') ? 'admin' : 'default';

  return (
    <div className="relative min-h-screen overflow-x-hidden font-sans" style={{ backgroundColor: 'var(--app-bg)', color: 'var(--app-text)' }}>
      {!isAdminRoute && <FluidBackground />}
      <div className="cursor-aura-layer z-[5]" data-theme={resolvedTheme} data-page={cursorPage}>
        <div ref={cursorPrimaryRef} className="cursor-aura cursor-aura-primary" />
        <div ref={cursorSecondaryRef} className="cursor-aura cursor-aura-secondary" />
      </div>

      <UserBar
        profile={profile}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        theme={resolvedTheme}
        onThemeToggle={() => setThemePreference(resolvedTheme === 'dark' ? 'light' : 'dark')}
        language={language}
        onLanguageToggle={() => {
          if (isLanguageSwitching) return;
          setIsLanguageSwitching(true);
          if (languageSwitchTimeoutRef.current) {
            window.clearTimeout(languageSwitchTimeoutRef.current);
          }
          setLanguage(language === 'pl' ? 'en' : 'pl');
          languageSwitchTimeoutRef.current = window.setTimeout(() => {
            setIsLanguageSwitching(false);
            languageSwitchTimeoutRef.current = null;
          }, 1200);
        }}
        isLanguageSwitching={isLanguageSwitching}
        t={t}
      />

      <main className="relative z-10 will-change-transform">
        <Outlet />
      </main>

      <footer className="pointer-events-auto fixed bottom-3 left-3 z-50 flex max-w-[calc(100vw-1.5rem)] flex-wrap items-center gap-2 text-[9px] font-black uppercase tracking-[0.12em] text-[var(--app-text-soft)]">
        <Link className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-2.5 py-1 transition-colors hover:text-[var(--app-text)]" to="/terms">
          {t('layout.terms')}
        </Link>
        <Link className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-2.5 py-1 transition-colors hover:text-[var(--app-text)]" to="/privacy">
          {t('layout.privacy')}
        </Link>
        <span className="hidden rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-2.5 py-1 sm:inline-flex">
          {t('layout.contact')}
        </span>
      </footer>

      <CookieBanner />
    </div>
  );
}
